import React from 'react';
import { Bar, Chart, Line, Doughnut } from 'react-chartjs-2';
import {
  baseArcOptions,
  baseCartesianOptions,
  dualAxisLineOptions,
  formatChartNumber
} from './chartTheme';

export function ReportChartCard({ title, subtitle, height = 300, children, className = '' }) {
  return (
    <article className={`hd-chart-card ${className}`.trim()}>
      <header className="hd-chart-card__head">
        <h3 className="hd-chart-card__title">{title}</h3>
        {subtitle ? <p className="hd-chart-card__subtitle">{subtitle}</p> : null}
      </header>
      <div className="hd-chart-card__body" style={{ height }}>
        {children}
      </div>
    </article>
  );
}

export function ClinicalBarChart({
  data,
  horizontal = false,
  yLabel,
  xLabel,
  showLegend = true,
  options = {},
  plugins = []
}) {
  const base = baseCartesianOptions({ horizontal, yLabel, xLabel });
  const chartOptions = {
    ...base,
    indexAxis: horizontal ? 'y' : 'x',
    ...options,
    onHover: options.onHover || base.onHover,
    onClick: options.onClick || base.onClick,
    plugins: {
      ...base.plugins,
      ...(options.plugins || {}),
      legend: {
        ...base.plugins?.legend,
        ...(options.plugins?.legend || {}),
        display: showLegend && options.plugins?.legend?.display !== false
      },
      tooltip: {
        ...base.plugins?.tooltip,
        ...(options.plugins?.tooltip || {}),
        callbacks: {
          ...base.plugins?.tooltip?.callbacks,
          ...(options.plugins?.tooltip?.callbacks || {})
        }
      }
    }
  };

  return <Bar data={data} options={chartOptions} plugins={plugins} />;
}

export function ClinicalBoxPlotChart({
  data,
  horizontal = false,
  yLabel,
  xLabel,
  options = {}
}) {
  const base = baseCartesianOptions({ horizontal, yLabel, xLabel });
  const chartOptions = {
    ...base,
    indexAxis: horizontal ? 'y' : 'x',
    plugins: {
      ...base.plugins,
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: '#0f172a',
        titleColor: '#f8fafc',
        bodyColor: '#e2e8f0',
        padding: 12,
        cornerRadius: 8,
        callbacks: {
          label(ctx) {
            const raw = ctx.raw || {};
            if (raw.min == null && raw.q1 == null && raw.median == null) {
              const parsed = ctx.parsed?.[horizontal ? 'x' : 'y'];
              return `${ctx.dataset.label || ''} : ${formatChartNumber(parsed)}`;
            }
            return [
              `Min : ${formatChartNumber(raw.min)}`,
              `Q1 : ${formatChartNumber(raw.q1)}`,
              `Médiane : ${formatChartNumber(raw.median)}`,
              `Q3 : ${formatChartNumber(raw.q3)}`,
              `Max : ${formatChartNumber(raw.max)}`
            ];
          }
        }
      }
    },
    ...options
  };

  return <Chart type="boxplot" data={data} options={chartOptions} />;
}

export function ClinicalLineChart({ data, yLabel, xLabel, y1Label, options = {} }) {
  const chartOptions = {
    ...(y1Label ? dualAxisLineOptions({ yLabel, y1Label, xLabel }) : baseCartesianOptions({ yLabel, xLabel })),
    ...options
  };

  return <Line data={data} options={chartOptions} />;
}

export function ClinicalDoughnutChart({ data, cutout, options = {}, plugins = [] }) {
  const base = baseArcOptions({ cutout });
  const chartOptions = {
    ...base,
    ...options,
    onHover: options.onHover || base.onHover,
    onClick: options.onClick || base.onClick,
    plugins: {
      ...base.plugins,
      ...(options.plugins || {}),
      legend: {
        ...base.plugins?.legend,
        ...(options.plugins?.legend || {})
      },
      tooltip: {
        ...base.plugins?.tooltip,
        ...(options.plugins?.tooltip || {}),
        callbacks: {
          ...base.plugins?.tooltip?.callbacks,
          ...(options.plugins?.tooltip?.callbacks || {})
        }
      }
    }
  };

  return <Doughnut data={data} options={chartOptions} plugins={plugins} />;
}

export function ReportsChartGrid({ children }) {
  return <div className="hd-reports-chart-grid row g-3 mb-4">{children}</div>;
}

export function ReportsChartCol({ lg = 6, children }) {
  return <div className={`col-12 col-lg-${lg}`}>{children}</div>;
}
